import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { BoltDirectivesModule } from '@nationwide-bolt/ng-directives';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';

import { EcifProductType } from '@hsc/common/schemas/ecif.schema.js';
import { FormField } from '@components/form-field';
import { QuotingFlowStore } from '@components/quoting-flow/services/quoting-flow.store';
import type { GisResponse, GisSuggestion } from '@components/quoting-flow/types/gis-types';
import { CUSTOM_VALIDATION_ERROR_MESSAGES, USER_NOTIFICATIONS } from '@constants/user-notifications';
import { FullStoryService } from '@services/fullstory.service';
import { GisService } from '@services/gis.service';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { SplunkLoggerService } from '@services/splunk-logger.service';
import { clearFormValidationError, collectFormValidationErrors, YES_NO } from '@utilities/form-utilities';

@Component({
  selector: 'hsc-property-review-step',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, ReactiveFormsModule, BoltDirectivesModule, FormField],
  templateUrl: './property-review.step.html',
})
export class PropertyReviewStep implements OnInit {
  // Services
  readonly qfStore = inject(QuotingFlowStore);
  private readonly fullStoryService = inject(FullStoryService);
  private readonly gisService = inject(GisService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly splunkLoggerService = inject(SplunkLoggerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pilotStatesEnabled = this.launchDarklyService.getFlagSignal<string[]>('pilot-states-enabled', []);
  private readonly mailingAddressInputSubject = new Subject<string>();

  // Component Properties
  readonly initialValues = this.qfStore.propertyReviewForm.getRawValue();
  readonly formValidationErrors = signal<Record<string, string>>({});
  readonly axiomQuoteState = this.qfStore.axiomQuoteState;

  readonly form = this.qfStore.propertyReviewForm;

  ngOnInit(): void {
    this.validateBusinessRules();

    this.mailingAddressInputSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((address) => this.gisService.getTypeAheadAddress(address)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          const suggestions = (result as GisResponse).response;

          this.qfStore.matchedAddresses.set([]);

          // If GIS doesn't find a match, it'll return a single record with only addressLine1 populated.
          // The record also has a match code that starts with 'E' to indicate an error or no match.
          // Successful matches generally have an empty string for match code.
          if (suggestions.length === 1 && suggestions[0].matchCode.toUpperCase().startsWith('E')) {
            this.qfStore.suggestedAddresses.set([]);
            this.form.controls.mailingAddress.setErrors({ addressNotFound: true });
            this.formValidationErrors.update((errors) => ({
              ...errors,
              mailingAddress: CUSTOM_VALIDATION_ERROR_MESSAGES.addressNotFound,
            }));
          } else {
            this.qfStore.suggestedAddresses.set(
              suggestions.map((address) => {
                const formattedAddress = this.gisService.formatSuggestedAddress(address);
                this.qfStore.matchedAddresses.update((addresses) => [...addresses, formattedAddress]);
                return {
                  address,
                  formattedAddress,
                };
              }),
            );
            this.form.controls.mailingAddress.setErrors(null);
            clearFormValidationError(this.formValidationErrors, 'mailingAddress');
          }
        },
        error: (error) => {
          this.qfStore.matchedAddresses.set([]);
          this.qfStore.suggestedAddresses.set([]);
          this.splunkLoggerService.error(error);
          this.formValidationErrors.update((errors) => ({
            ...errors,
            mailingAddress: CUSTOM_VALIDATION_ERROR_MESSAGES.addressNotFound,
          }));
        },
      });
  }

  private trackIneligibleError(error: string, extraProps: Record<string, string | number | undefined> = {}): void {
    this.fullStoryService.event('Property Review Ineligible', { reason: error, ...extraProps });
  }

  private hasValidDifferentMailingAddress(): boolean {
    if (!this.qfStore.isDifferentMailingAddress()) {
      return true;
    }

    const { customer, property } = this.form.getRawValue();
    // A disabled form always reports .valid as false, so only check validity while editable
    if (this.form.controls.customer.enabled && !this.form.controls.customer.valid) {
      return false;
    }

    const normalizeAddressValue = (value: string | null | undefined) => value?.trim().toUpperCase() ?? '';

    const someMismatch = [
      normalizeAddressValue(customer.addressLine1) !== normalizeAddressValue(property.addressLine1),
      normalizeAddressValue(customer.addressLine2) !== normalizeAddressValue(property.addressLine2),
      normalizeAddressValue(customer.city) !== normalizeAddressValue(property.city),
      normalizeAddressValue(customer.country) !== normalizeAddressValue(property.country),
      normalizeAddressValue(customer.stateProvinceRegion) !== normalizeAddressValue(property.stateProvinceRegion),
      normalizeAddressValue(customer.zipPostalCode) !== normalizeAddressValue(property.zipPostalCode),
    ].some(Boolean);

    return someMismatch;
  }

  // Methods
  // Validate on load and upon submission due to possible manual input
  // Errors are ordered by how fields display in html
  private validateBusinessRules(): boolean {
    const values = this.form.getRawValue();

    const customerPropertyState = values.property.stateProvinceRegion?.trim().toUpperCase();
    const enabledStates = this.pilotStatesEnabled().map((s) => s.toUpperCase());
    if (!enabledStates.includes(customerPropertyState)) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.pilotStateNotEligible, 'property.stateProvinceRegion');
      this.trackIneligibleError('pilotStateNotEligible', { state: customerPropertyState });
      return false;
    }
    if (!this.hasValidDifferentMailingAddress()) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.mailingAddressNotValid, 'mailingAddress');
      this.trackIneligibleError('mailingAddressNotValid');
      return false;
    }
    if (values.propertyType !== EcifProductType.Homeowner) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.policyNotHomeowners, 'propertyType');
      this.trackIneligibleError('policyNotHomeowners', { propertyType: values.propertyType });
      return false;
    }
    if (values.propertySquareFootage && values.propertySquareFootage > 5000) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.propertySquareFootageNotEligible, 'propertySquareFootage');
      this.trackIneligibleError('propertySquareFootageNotEligible', { squareFootage: values.propertySquareFootage });
      return false;
    }
    if (values.isTownhouse === YES_NO.YES) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.townhouseNotEligible, 'isTownhouse');
      this.trackIneligibleError('townhouseNotEligible');
      return false;
    }
    if (values.isRegisteredHistoric === YES_NO.YES) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.registeredHistoricNotEligible, 'isRegisteredHistoric');
      this.trackIneligibleError('registeredHistoricNotEligible');
      return false;
    }
    if (values.isMobileOrManufactured === YES_NO.YES) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.mobileOrManufacturedNotEligible, 'isMobileOrManufactured');
      this.trackIneligibleError('mobileOrManufacturedNotEligible');
      return false;
    }
    if (values.hasEquipmentBreakdown === YES_NO.YES) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.equipmentBreakdownNotEligible, 'hasEquipmentBreakdown');
      this.trackIneligibleError('equipmentBreakdownNotEligible');
      return true; // Allow user to continue with warning
    }

    this.qfStore.setNotificationState(null);
    return true;
  }

  // Allow optionality for possible invalid email addresses to be editable
  // Display as view if valid (or form disabled), otherwise undefined so it can be edited
  private readonly emailControl = this.form.controls.customer.controls.emailAddress;
  readonly emailDisplayValue =
    this.emailControl.valid || this.emailControl.disabled ? this.emailControl.value : undefined;
  readonly mailingAddressIsDisabled = this.form.disabled;
  readonly mailingAddressDisplayValue = this.mailingAddressIsDisabled
    ? this.formatMailingAddress(this.form.controls.customer.getRawValue())
    : '';

  private formatMailingAddress(address: {
    addressLine1: string;
    addressLine2: string | null | undefined;
    city: string;
    stateProvinceRegion: string;
    zipPostalCode: string;
  }): string {
    return [
      address.addressLine1,
      address.addressLine2,
      [address.city, address.stateProvinceRegion].filter(Boolean).join(', '),
      address.zipPostalCode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  private get customFormValidationErrorMessages() {
    return {
      propertySquareFootage: {
        min: CUSTOM_VALIDATION_ERROR_MESSAGES.minimumSquareFeet,
      },
    };
  }

  private validatePropertyReviewForm(): boolean {
    this.form.markAllAsTouched();
    const errors = collectFormValidationErrors(this.form, this.customFormValidationErrorMessages);
    if (this.form.controls.mailingAddress.hasError('addressNotFound')) {
      errors['mailingAddress'] = CUSTOM_VALIDATION_ERROR_MESSAGES.addressNotFound;
    }
    if (Object.keys(errors).length > 0) {
      this.formValidationErrors.set(errors);
      return false;
    }

    // Business rules run after field validation passes
    return this.validateBusinessRules();
  }

  // Make accessible per html field
  clearFormValidationError(key: string): void {
    clearFormValidationError(this.formValidationErrors, key);
  }

  handleForward(): void {
    if (!this.validatePropertyReviewForm()) return;
    this.fullStoryService.event('Property Review Advanced');
    this.qfStore.handleForward();
  }

  handleBackward(): void {
    this.qfStore.handleBackward();
  }

  setMailingAddress(event: Event | GisSuggestion): void {
    let matchedAddress: GisSuggestion | undefined;

    if ('formattedAddress' in event && 'address' in event) {
      matchedAddress = event;
    } else {
      const inputValue = (event.target as HTMLInputElement)?.value || '';
      matchedAddress = this.qfStore.suggestedAddresses().find((address) => address.formattedAddress === inputValue);
    }

    if (matchedAddress) {
      this.qfStore.propertyReviewForm.patchValue({
        customer: {
          addressLine1: matchedAddress.address.addressLine,
          addressLine2: matchedAddress.address.addressLine2 || undefined,
          city: matchedAddress.address.city,
          stateProvinceRegion: matchedAddress.address.state,
          zipPostalCode: matchedAddress.address.zip,
        },
        mailingAddress: matchedAddress.formattedAddress,
      });
    }
  }

  setMailingAddressList(event: Event): void {
    const inputValue = (event.target as HTMLInputElement)?.value.trim();

    const exactMatch = this.qfStore
      .suggestedAddresses()
      .find((suggestion) => suggestion.formattedAddress === inputValue);

    // Exact match will be true if someone clicked or pressed enter on one of the suggested addresses, we do not need (or want) to trigger the input subject again
    if (exactMatch) {
      this.setMailingAddress(exactMatch);
      return;
    }

    if (inputValue) {
      this.mailingAddressInputSubject.next(inputValue);
    }
  }

  toggleDifferentMailingAddress(): void {
    const newValue = !this.qfStore.isDifferentMailingAddress();

    if (!newValue) {
      // reset mailing address fields to match property fields
      const property = this.qfStore.propertyReviewForm.get('property')?.value;
      this.qfStore.propertyReviewForm.patchValue({
        customer: {
          addressLine1: property?.addressLine1 || '',
          addressLine2: property?.addressLine2 || undefined, // Set undefined (not empty string) so it's omitted from the API request.
          city: property?.city || '',
          stateProvinceRegion: property?.stateProvinceRegion || '',
          zipPostalCode: property?.zipPostalCode || '',
        },
        mailingAddress: '',
      });
    }

    this.qfStore.isDifferentMailingAddress.set(newValue);
  }
}
